import React, { useState, useEffect } from 'react';
import '../../styles/Admin.css';

function Logs() {
  const [stats, setStats] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

  useEffect(() => {
    cargarDatos();
    
    // Auto-refresh cada 30 segundos
    const interval = setInterval(() => {
      cargarDatos();
    }, 30000);

    return () => clearInterval(interval);
  }, [refreshKey]);

  const cargarDatos = async () => {
    try {
      setLoading(true);

      // Obtener datos del usuario para autenticación
      const usuarioGuardado = sessionStorage.getItem('usuario');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      if (usuarioGuardado) {
        try {
          const usuario = JSON.parse(usuarioGuardado);
          if (usuario.dni) {
            headers['x-user-dni'] = usuario.dni;
          }
        } catch (error) {
          console.error('Error parsing usuario:', error);
        }
      }

      // Cargar estadísticas
      const statsResponse = await fetch(`${API_URL}/logs/stats`, { headers });
      const statsData = await statsResponse.json();
      
      if (statsData.success === false) {
        console.error('Error en stats:', statsData.error);
        return;
      }
      
      setStats(statsData.stats);

      // Cargar últimos errores
      const errorsResponse = await fetch(`${API_URL}/logs/errors?limit=20`, { headers });
      const errorsData = await errorsResponse.json();
      
      if (errorsData.success === false) {
        console.error('Error en errors:', errorsData.error);
        return;
      }
      
      setErrors(errorsData.errors || []);

    } catch (error) {
      console.error('Error cargando logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirDashboard = () => {
    const usuarioGuardado = sessionStorage.getItem('usuario');
    let url = `${API_URL}/logs/dashboard`;
    
    if (usuarioGuardado) {
      try {
        const usuario = JSON.parse(usuarioGuardado);
        if (usuario.dni) {
          url += `?dni=${encodeURIComponent(usuario.dni)}`;
        }
      } catch (error) {
        console.error('Error parsing usuario:', error);
      }
    }
    
    window.open(url, '_blank');
  };

  const formatearFecha = (timestamp) => {
    return new Date(timestamp).toLocaleString('es-AR');
  };

  if (loading && !stats) {
    return (
      <div className="container">
        <div className="loading">Cargando datos...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="page-header">
        <h1>📊 Monitoreo de Errores</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="btn btn-primary"
            onClick={() => setRefreshKey(prev => prev + 1)}
          >
            🔄 Actualizar
          </button>
          <button 
            className="btn btn-secondary"
            onClick={abrirDashboard}
          >
            📈 Dashboard Completo
          </button>
        </div>
      </div>

      {/* Tarjetas de Estadísticas */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '20px',
        marginBottom: '30px'
      }}>
        <div className="stat-card" style={{
          background: '#fff',
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #e74c3c'
        }}>
          <h3 style={{ color: '#999', fontSize: '14px', marginBottom: '10px' }}>
            Total de Errores
          </h3>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#e74c3c' }}>
            {stats?.count || 0}
          </div>
        </div>

        <div className="stat-card" style={{
          background: '#fff',
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #f39c12'
        }}>
          <h3 style={{ color: '#999', fontSize: '14px', marginBottom: '10px' }}>
            Tipos de Error
          </h3>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f39c12' }}>
            {stats ? Object.keys(stats.errorsByType || {}).length : 0}
          </div>
        </div>

        <div className="stat-card" style={{
          background: '#fff',
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #3498db'
        }}>
          <h3 style={{ color: '#999', fontSize: '14px', marginBottom: '10px' }}>
            Último Error
          </h3>
          <div style={{ fontSize: '14px', color: '#3498db' }}>
            {stats?.lastError ? 
              formatearFecha(stats.lastError.timestamp) : 
              'Ninguno'
            }
          </div>
        </div>
      </div>

      {/* Errores por Tipo */}
      {stats && Object.keys(stats.errorsByType || {}).length > 0 && (
        <div className="section" style={{ marginBottom: '30px' }}>
          <h2>🔴 Errores por Tipo</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Tipo de Error</th>
                  <th style={{ textAlign: 'right' }}>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.errorsByType)
                  .sort((a, b) => b[1] - a[1])
                  .map(([tipo, cantidad]) => (
                    <tr key={tipo}>
                      <td><code>{tipo}</code></td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {cantidad}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Errores por Endpoint */}
      {stats && Object.keys(stats.errorsByEndpoint || {}).length > 0 && (
        <div className="section" style={{ marginBottom: '30px' }}>
          <h2>🔗 Errores por Endpoint</h2>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Endpoint</th>
                  <th style={{ textAlign: 'right' }}>Cantidad</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(stats.errorsByEndpoint)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 10)
                  .map(([endpoint, cantidad]) => (
                    <tr key={endpoint}>
                      <td><code>{endpoint}</code></td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold' }}>
                        {cantidad}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Últimos Errores */}
      <div className="section">
        <h2>⚠️ Últimos Errores Registrados</h2>
        
        {errors.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            background: '#f8f9fa',
            borderRadius: '10px',
            color: '#666'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '10px' }}>✅</div>
            <p>No hay errores recientes</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            {errors.map((error, index) => (
              <div 
                key={index}
                style={{
                  padding: '15px',
                  background: '#fff5f5',
                  borderLeft: '4px solid #e74c3c',
                  borderRadius: '5px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'start',
                  marginBottom: '10px'
                }}>
                  <div>
                    <strong style={{ color: '#e74c3c', fontSize: '16px' }}>
                      {error.message || error.raw}
                    </strong>
                    {error.level && (
                      <span style={{
                        marginLeft: '10px',
                        padding: '2px 8px',
                        background: '#e74c3c',
                        color: 'white',
                        borderRadius: '3px',
                        fontSize: '12px',
                        textTransform: 'uppercase'
                      }}>
                        {error.level}
                      </span>
                    )}
                  </div>
                  <span style={{ 
                    fontSize: '12px', 
                    color: '#999',
                    whiteSpace: 'nowrap'
                  }}>
                    {error.timestamp && formatearFecha(error.timestamp)}
                  </span>
                </div>
                
                {error.url && (
                  <div style={{ fontSize: '14px', color: '#666', marginBottom: '5px' }}>
                    🔗 <code>{error.method} {error.url}</code>
                  </div>
                )}
                
                {error.userDNI && (
                  <div style={{ fontSize: '14px', color: '#666' }}>
                    👤 DNI: {error.userDNI}
                  </div>
                )}
                
                {error.stack && (
                  <details style={{ marginTop: '10px' }}>
                    <summary style={{ 
                      cursor: 'pointer', 
                      color: '#3498db',
                      fontSize: '13px'
                    }}>
                      Ver Stack Trace
                    </summary>
                    <pre style={{
                      marginTop: '10px',
                      padding: '10px',
                      background: '#f8f9fa',
                      borderRadius: '5px',
                      fontSize: '11px',
                      overflow: 'auto',
                      maxHeight: '200px'
                    }}>
                      {error.stack}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ 
        marginTop: '30px', 
        textAlign: 'center', 
        color: '#999',
        fontSize: '14px'
      }}>
        <p>
          Los datos se actualizan automáticamente cada 30 segundos
        </p>
        <p>
          Para ver análisis detallados, abre el 
          <button 
            onClick={abrirDashboard}
            style={{
              background: 'none',
              border: 'none',
              color: '#3498db',
              textDecoration: 'underline',
              cursor: 'pointer',
              marginLeft: '5px'
            }}
          >
            Dashboard Completo
          </button>
        </p>
      </div>
    </div>
  );
}

export default Logs;
